import {
  formatAccountCurrency,
  formatAccountCurrencyCompact,
} from '@/lib/currency'
import { getActiveLocale } from '@/lib/locale'
import i18n from '@/i18n'

export function formatCurrency(value: number): string {
  return formatAccountCurrency(value || 0)
}

/** True when formatCurrencyCompact abbreviates, so the exact value is worth showing */
export function isCurrencyAbbreviated(value: number): boolean {
  return Math.abs(value || 0) >= 10_000
}

/** KPI cards need short values: a full "R$ 1.487.500,00" breaks the card layout. */
export function formatCurrencyCompact(value: number): string {
  return formatAccountCurrencyCompact(value || 0)
}

export function formatNumber(value: number): string {
  return (value || 0).toLocaleString(getActiveLocale())
}

/** Averages and rates that are not percentages — one decimal place, account locale. */
export function formatDecimal(value: number, fractionDigits = 1): string {
  return (value || 0).toLocaleString(getActiveLocale(), { maximumFractionDigits: fractionDigits })
}

export function formatPercent(value: number, fractionDigits = 1): string {
  return `${(value || 0).toLocaleString(getActiveLocale(), { maximumFractionDigits: fractionDigits })}%`
}

export type DeltaTone = 'positive' | 'negative' | 'neutral'

export type Delta = {
  /** null when there is no comparable baseline */
  percent: number | null
  tone: DeltaTone
  label: string
}

/**
 * A percentage change needs a baseline to mean anything. When the previous period had
 * nothing, "+100%" would be noise, so we say "novo" instead of inventing a ratio.
 */
export function computeDelta(
  current: number,
  previous: number,
  lowerIsBetter = false,
): Delta {
  const t = i18n.getFixedT(null, 'common')

  if (!previous) {
    if (!current) return { percent: null, tone: 'neutral', label: t('dashboard.noBaseline') }
    return {
      percent: null,
      tone: lowerIsBetter ? 'negative' : 'positive',
      label: t('dashboard.newInPeriod'),
    }
  }

  const percent = ((current - previous) / Math.abs(previous)) * 100
  const rounded = Math.round(percent * 10) / 10

  if (rounded === 0) return { percent: 0, tone: 'neutral', label: t('dashboard.stable') }

  const improved = lowerIsBetter ? rounded < 0 : rounded > 0

  return {
    percent: rounded,
    tone: improved ? 'positive' : 'negative',
    label: `${rounded > 0 ? '+' : ''}${rounded.toLocaleString(getActiveLocale(), {
      maximumFractionDigits: 1,
    })}%`,
  }
}

export function formatIdleDays(days: number | null): string | null {
  if (days === null || days === undefined) return null
  const t = i18n.getFixedT(null, 'common')
  if (days < 1) return t('dashboard.idleToday')
  return t('dashboard.idleDays', { count: Math.round(days) })
}
